import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Camera, Heart, X } from 'lucide-react';
import { toast } from 'sonner';
import { MAX_PHOTO_SIZE_BYTES, MAX_PHOTO_SIZE_MB, MIN_AGE, MAX_AGE, MAX_INTERESTS, PREFERENCE_OPTIONS } from '../constants';
import { compressImage } from '../lib/compressImage';
import GalleryEditor from '../components/GalleryEditor';

const ProfileSetup = () => {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('female');
  const [relationshipStatus, setRelationshipStatus] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [interests, setInterests] = useState([]);
  const [interestInput, setInterestInput] = useState('');
  const [preferences, setPreferences] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.error('Image too large', { description: `Please choose an image under ${MAX_PHOTO_SIZE_MB}MB.` });
      return;
    }
    try { setPhoto(await compressImage(file)); }
    catch { toast.error("Couldn't read that image. Try a different one?"); }
  };

  const addInterest = () => {
    const v = interestInput.trim();
    if (!v || interests.includes(v) || interests.length >= MAX_INTERESTS) return;
    setInterests([...interests, v]);
    setInterestInput('');
  };

  const removeInterest = (i) => setInterests(interests.filter((x) => x !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !age || !gender) {
      toast.error('Missing fields', { description: 'Please complete name, age, and gender.' });
      return;
    }
    const ageNum = parseInt(age, 10);
    if (ageNum < MIN_AGE || ageNum > MAX_AGE) {
      toast.error('Invalid age', { description: `You must be ${MIN_AGE} or older.` });
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.put('/profile', {
        name: name.trim(),
        age: ageNum,
        gender,
        relationship_status: relationshipStatus || null,
        bio: bio.trim(),
        location: location.trim(),
        interests,
        preferences,
        photo_base64: photo,
        gallery_photos: gallery,
      });
      await refresh();
      toast.success('Profile saved!', { description: "Welcome to Flirty." });
      navigate('/browse');
    } catch (err) {
      toast.error('Failed to save', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-fnl-dark relative overflow-hidden py-10 px-4">
      <div className="absolute top-0 left-0 w-96 h-96 bg-fnl-pink/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-700/30 rounded-full blur-3xl" />

      <div className="relative max-w-2xl mx-auto">
        <div className="flex items-center gap-2 justify-center mb-6">
          <div className="relative">
            <span className="text-white font-bold text-3xl">F</span>
            <Heart className="absolute -top-1 -right-3 w-4 h-4 fill-fnl-pink text-fnl-pink" />
          </div>
          <span className="text-white font-semibold text-lg">Flirty</span>
        </div>

        <div className="bg-fnl-purple/80 backdrop-blur-md rounded-2xl p-7 md:p-9 border border-purple-900/40">
          <h1 className="text-3xl font-bold text-white mb-1">Create your profile</h1>
          <p className="text-purple-200 text-sm mb-7">Tell others a bit about you so we can find your perfect match.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Photo */}
            <div className="flex items-center gap-5">
              <div
                onClick={() => fileRef.current?.click()}
                className="relative w-24 h-24 rounded-full bg-fnl-purple-light border-2 border-dashed border-fnl-pink/60 flex items-center justify-center cursor-pointer overflow-hidden hover:border-fnl-pink"
              >
                {photo ? (
                  <img src={photo} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-fnl-pink" />
                )}
              </div>
              <div>
                <p className="text-white text-sm font-medium">Profile Photo</p>
                <p className="text-purple-300 text-xs">JPG/PNG up to {MAX_PHOTO_SIZE_MB}MB — large photos auto-resized</p>
                <input type="file" accept="image/*" ref={fileRef} onChange={handlePhoto} className="hidden" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white mb-2 block">Name</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your display name" className="bg-white border-0 h-11" />
              </div>
              <div>
                <Label className="text-white mb-2 block">Age</Label>
                <Input type="number" min={MIN_AGE} max={MAX_AGE} value={age} onChange={(e) => setAge(e.target.value)} placeholder={`${MIN_AGE}+`} className="bg-white border-0 h-11" />
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Gender</Label>
              <RadioGroup value={gender} onValueChange={setGender} className="flex gap-4">
                {['female', 'male', 'other'].map((g) => (
                  <div key={g} className="flex items-center gap-2">
                    <RadioGroupItem value={g} id={g} className="border-fnl-pink text-fnl-pink" />
                    <Label htmlFor={g} className="text-purple-100 capitalize cursor-pointer">{g}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div>
              <Label className="text-white mb-2 block">Status <span className="text-purple-300 text-xs font-normal">(your relationship status)</span></Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="status-options">
                {['single', 'married', 'divorced', 'other'].map((s) => {
                  const active = relationshipStatus === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setRelationshipStatus(active ? '' : s)}
                      data-testid={`status-${s}`}
                      className={
                        active
                          ? 'px-2 py-2 rounded-lg text-xs font-semibold capitalize bg-fnl-pink text-white border border-fnl-pink shadow-[0_4px_12px_rgba(238,42,123,0.35)] transition'
                          : 'px-2 py-2 rounded-lg text-xs font-medium capitalize bg-fnl-purple-light/40 text-purple-200 border border-purple-800/40 hover:border-fnl-pink hover:text-white transition'
                      }
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-white mb-2 block">Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, Country" className="bg-white border-0 h-11" />
            </div>

            <div>
              <Label className="text-white mb-2 block">Bio</Label>
              <Textarea value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Tell others about yourself..." rows={4} className="bg-white border-0" />
            </div>

            <div>
              <Label className="text-white mb-2 block">Interests</Label>
              <div className="flex gap-2">
                <Input
                  value={interestInput}
                  onChange={(e) => setInterestInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInterest(); } }}
                  placeholder="e.g. Travel, Music, Cooking..."
                  className="bg-white border-0 h-11 flex-1"
                />
                <Button type="button" onClick={addInterest} className="bg-fnl-pink hover:bg-fnl-pink h-11">Add</Button>
              </div>
              {interests.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {interests.map((i) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-fnl-pink/20 text-fnl-pink text-sm border border-fnl-pink/40">
                      {i}
                      <button type="button" onClick={() => removeInterest(i)} className="hover:text-white"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-white mb-2 block">Interest-Into <span className="text-purple-300 text-xs font-normal">(select what you're into)</span></Label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {PREFERENCE_OPTIONS.map((p) => {
                  const active = preferences.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPreferences(active ? preferences.filter((x) => x !== p) : [...preferences, p])}
                      className={
                        active
                          ? 'px-2 py-2 rounded-lg text-xs font-semibold bg-fnl-pink text-white border border-fnl-pink shadow-[0_4px_12px_rgba(238,42,123,0.35)] transition'
                          : 'px-2 py-2 rounded-lg text-xs font-medium bg-fnl-purple-light/40 text-purple-200 border border-purple-800/40 hover:border-fnl-pink hover:text-white transition'
                      }
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>

            <GalleryEditor photos={gallery} onChange={setGallery} />

            <Button type="submit" disabled={submitting} className="w-full h-12 bg-fnl-pink hover:bg-fnl-pink text-white font-semibold btn-shadow-pink mt-2">
              {submitting ? 'Saving...' : 'Complete Profile'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
